import { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { Bot, Cloud, Activity, Link2, X, Check } from 'lucide-react'
import { API_URL } from '../../config/api'

// ─── Types ────────────────────────────────────────────────────────────────────

interface AgentConfig {
  agent_id: number; agent_name: string; persona?: string
  expertise?: string; model: string; fallback_model?: string
}

interface ExpertItem {
  id: number; name: string; expertise: string; cf_model: string; fallback: string
}

interface CFModel {
  id: string; name: string; free: boolean
}

interface LLMConfigData {
  anne_filiz: AgentConfig
  baba_filiz: AgentConfig
  experts: ExpertItem[]
  bebek_filizler: { count: number; cf_model: string; fallback: string }
  askai: AgentConfig
  cf_available_models: CFModel[]
  cf_connected?: boolean
}

// ─── Constants ────────────────────────────────────────────────────────────────

const authHdr = () => ({ Authorization: `Bearer ${localStorage.getItem('token') ?? ''}` })

// ─── Styles ───────────────────────────────────────────────────────────────────

const card = {
  background: 'rgba(30,30,40,0.7)', border: '1px solid rgba(255,255,255,0.08)',
  borderRadius: '12px', padding: '20px',
} as const

const select = {
  width: '100%', padding: '8px 10px', background: 'rgba(0,0,0,0.3)',
  border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px',
  color: '#e2e8f0', fontSize: '13px', outline: 'none',
} as const

const badge = (color: string) => ({
  display: 'inline-block', padding: '2px 8px', borderRadius: '4px',
  fontSize: '10px', fontWeight: 700, background: `${color}20`, color,
  textTransform: 'uppercase' as const, letterSpacing: '0.5px',
})

// ─── Component ────────────────────────────────────────────────────────────────

export default function AdminLLMConfig() {
  const [data, setData] = useState<LLMConfigData | null>(null)
  const [loading, setLoading] = useState(true)
  const [models, setModels] = useState<CFModel[]>([])
  const [cfModalOpen, setCfModalOpen] = useState(false)
  const [cfToken, setCfToken] = useState('')
  const [cfEmail, setCfEmail] = useState('')
  const [cfConnecting, setCfConnecting] = useState(false)
  const [cfConnected, setCfConnected] = useState(false)

  useEffect(() => { void loadConfig() }, [])

  const handleCFConnect = async () => {
    if (!cfToken || !cfEmail) {
      toast.error('Token ve E-mail adresi zorunlu')
      return
    }
    setCfConnecting(true)
    try {
      // Kendi orijinal endpoint'ine donuyoruz (500 hatasi backend'de tamamen izole edilip cozuldu)
      const res = await fetch(`${API_URL}/api/admin/llm-config/cf-connect`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${localStorage.getItem('token') || ''}`,
          'Content-Type': 'application/json' 
        },
        body: JSON.stringify({
          api_token: cfToken,
          auth_email: cfEmail,
          account_id: "",
          use_global_key: true // Çalışan sayfadaki can alıcı parametre!
        }),
      })
      if (!res.ok) {
        const errData = await res.json().catch(() => ({ detail: `Sunucu Hatası: ${res.status}` }));
        throw new Error(errData.detail || 'Cloudflare bağlanamadı');
      }
      toast.success('CF bağlandı! Modeller artık production üzerinden çalışacak')
      setCfConnected(true)
      setTimeout(() => setCfModalOpen(false), 1500)
    } catch (e: any) {
      toast.error(e.message || 'CF bağlantı başarısız')
      console.error(e)
    } finally {
      setCfConnecting(false)
    }
  }

  const loadConfig = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/llm-config`, { headers: authHdr() })
      if (!res.ok) {throw new Error(`${res.status}`)}
      const json = await res.json() as LLMConfigData
      setData(json)
      setModels(json.cf_available_models || [])
      setCfConnected(json.cf_connected || false)
    } catch (e) {
      toast.error('LLM config yüklenemedi')
      console.error(e)
    } finally {
      setLoading(false)
    }
  }

  const updateAgent = async (agentId: number, cfModel: string) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/llm-config/agent/${agentId}`, {
        method: 'POST', headers: { ...authHdr(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ cf_model: cfModel }),
      })
      if (!res.ok) {throw new Error(`${res.status}`)}
      toast.success('Model güncellendi')
      void loadConfig()
    } catch { toast.error('Güncelleme başarısız') }
  }

  const updateBebek = async (cfModel: string) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/llm-config/bebek/bulk`, {
        method: 'POST', headers: { ...authHdr(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ cf_model: cfModel }),
      })
      if (!res.ok) {throw new Error(`${res.status}`)}
      const json = await res.json() as { updated: number }
      toast.success(`${json.updated} bebek güncellendi`)
      void loadConfig()
    } catch { toast.error('Toplu güncelleme başarısız') }
  }

  if (loading) {return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
      <div style={{ width: 40, height: 40, border: '3px solid rgba(16,185,129,0.3)',
        borderTopColor: '#10b981', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
    </div>
  )}

  if (!data) {return <div style={{ textAlign: 'center', padding: 40, color: '#888' }}>Veri yüklenemedi</div>}

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: '24px 16px', color: '#e2e8f0' }}>
      {/* Header */}
      <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 700, margin: 0 }}>
            <Bot size={22} style={{ verticalAlign: 'middle', marginRight: 8, color: '#10b981' }} />
            Admin LLM & Platform Yönetimi
          </h1>
          <p style={{ fontSize: 13, color: '#888', marginTop: 4 }}>
            Agent model routing, Cloud Provider bağlantıları, CF Workers AI
          </p>
        </div>
        <button
          onClick={() => setCfModalOpen(true)}
          style={{
            display: 'flex', alignItems: 'center', gap: 8, padding: '10px 18px',
            background: cfConnected ? '#10b981' : 'linear-gradient(135deg, #f38020, #fa8506)',
            border: 'none', borderRadius: 8, color: 'white', fontWeight: 600,
            fontSize: 13, cursor: 'pointer', boxShadow: cfConnected ? '0 4px 12px rgba(16,185,129,0.3)' : '0 4px 12px rgba(243,128,32,0.3)',
          }}
        >
          {cfConnected ? <Check size={16} /> : <Link2 size={16} />}
          {cfConnected ? 'CF Bağlı (Güncelle)' : 'CF Connect'}
        </button>
      </div>

      {/* CF Workers AI Models */}
      <div style={{ ...card, marginBottom: 16 }}>
        <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
          <Activity size={16} color="#10b981" /> CF Workers AI Modelleri
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {models.map(m => (
            <div key={m.id} style={{ padding: '6px 12px', borderRadius: 6,
              background: m.free ? 'rgba(16,185,129,0.1)' : 'rgba(245,158,11,0.1)',
              border: `1px solid ${m.free ? 'rgba(16,185,129,0.3)' : 'rgba(245,158,11,0.3)'}`,
              fontSize: 11, color: m.free ? '#10b981' : '#f59e0b' }}>
              {m.name} {m.free ? '(Free)' : '(Paid)'}
            </div>
          ))}
        </div>
      </div>

      {/* Agent Model Routing */}
      <div style={{ ...card }}>
        <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
          <Bot size={16} color="#8b5cf6" /> Agent Model Routing
        </div>

        {/* Anne + Baba + AskAI → Core Agents */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 12, marginBottom: 16 }}>
          {[
            { label: 'Anne Filiz', desc: 'EQ System, Onay', cfg: data.anne_filiz, color: '#ec4899' },
            { label: 'Baba Filiz', desc: 'Task Orchestrator', cfg: data.baba_filiz, color: '#3b82f6' },
            { label: 'AskAI Expert', desc: 'Support, Knowledge', cfg: data.askai, color: '#f59e0b' },
          ].map(item => (
            <div key={item.label} style={{ padding: 14, background: 'rgba(0,0,0,0.2)', borderRadius: 10,
              border: `1px solid ${item.color}30` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 14, color: item.color }}>{item.label}</div>
                  <div style={{ fontSize: 11, color: '#888' }}>{item.desc}</div>
                </div>
                <span style={badge(item.color)}>ID {item.cfg.agent_id}</span>
              </div>
              <div style={{ fontSize: 11, color: '#aaa', marginBottom: 6 }}>Mevcut: {item.cfg.model}</div>
              <select
                style={select}
                defaultValue={item.cfg.model}
                onChange={e => void updateAgent(item.cfg.agent_id, e.target.value)}
              >
                <option value={item.cfg.model}>{item.cfg.model} (mevcut)</option>
                {models.map(m => (
                  <option key={m.id} value={m.id}>{m.name} {m.free ? '' : '($)'}</option>
                ))}
              </select>
            </div>
          ))}
        </div>

        {/* Bebek Filizler → Bulk */}
        <div style={{ padding: 14, background: 'rgba(0,0,0,0.2)', borderRadius: 10,
          border: '1px solid rgba(16,185,129,0.2)', marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <div>
              <div style={{ fontWeight: 600, fontSize: 14, color: '#10b981' }}>Bebek Filizler</div>
              <div style={{ fontSize: 11, color: '#888' }}>{data.bebek_filizler.count} demo agent — toplu güncelleme</div>
            </div>
            <span style={badge('#10b981')}>{data.bebek_filizler.count} agent</span>
          </div>
          <div style={{ fontSize: 11, color: '#aaa', marginBottom: 6 }}>Mevcut: {data.bebek_filizler.cf_model}</div>
          <select
            style={select}
            defaultValue={data.bebek_filizler.cf_model}
            onChange={e => void updateBebek(e.target.value)}
          >
            <option value={data.bebek_filizler.cf_model}>{data.bebek_filizler.cf_model} (mevcut)</option>
            {models.map(m => (
              <option key={m.id} value={m.id}>{m.name} {m.free ? '' : '($)'}</option>
            ))}
          </select>
        </div>

        {/* Expert Agents Table */}
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8, color: '#c084fc' }}>
          Uzman Agentlar ({data.experts.length})
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                <th style={{ textAlign: 'left', padding: '8px 10px', color: '#888', fontWeight: 500 }}>ID</th>
                <th style={{ textAlign: 'left', padding: '8px 10px', color: '#888', fontWeight: 500 }}>Agent</th>
                <th style={{ textAlign: 'left', padding: '8px 10px', color: '#888', fontWeight: 500 }}>Uzmanlık</th>
                <th style={{ textAlign: 'left', padding: '8px 10px', color: '#888', fontWeight: 500 }}>Model</th>
                <th style={{ textAlign: 'left', padding: '8px 10px', color: '#888', fontWeight: 500 }}>Değiştir</th>
              </tr>
            </thead>
            <tbody>
              {data.experts.map(ex => (
                <tr key={ex.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                  <td style={{ padding: '8px 10px', color: '#888' }}>{ex.id}</td>
                  <td style={{ padding: '8px 10px', fontWeight: 500 }}>{ex.name}</td>
                  <td style={{ padding: '8px 10px', color: '#aaa', fontSize: 11 }}>{ex.expertise?.slice(0, 40)}</td>
                  <td style={{ padding: '8px 10px' }}>
                    <span style={badge('#8b5cf6')}>{ex.cf_model}</span>
                  </td>
                  <td style={{ padding: '8px 10px' }}>
                    <select
                      style={{ ...select, width: 200 }}
                      defaultValue={ex.cf_model}
                      onChange={e => void updateAgent(ex.id, e.target.value)}
                    >
                      <option value={ex.cf_model}>{ex.cf_model}</option>
                      {models.map(m => (
                        <option key={m.id} value={m.id}>{m.name}</option>
                      ))}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* CF Connect Modal */}
      {cfModalOpen && (
        <div
          onClick={() => !cfConnecting && setCfModalOpen(false)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999,
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              width: '90%', maxWidth: 480, background: '#1a1a24',
              border: '1px solid rgba(243,128,32,0.3)', borderRadius: 16, padding: 28,
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <Cloud size={22} color="#f38020" />
                <h2 style={{ margin: 0, fontSize: 18 }}>Cloudflare Connect (Admin)</h2>
              </div>
              <button
                onClick={() => setCfModalOpen(false)}
                style={{ background: 'none', border: 'none', color: '#888', cursor: 'pointer' }}
              >
                <X size={20} />
              </button>
            </div>

            <p style={{ fontSize: 12, color: '#888', marginBottom: 16, lineHeight: 1.5 }}>
              Sistem genelinde CF Workers AI kullanımı için global token gir. Bu token tüm admin agent&apos;ların (Anne, Baba, AskAI, 22 Uzman) production model çağrılarında kullanılır.
            </p>

            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 11, color: '#aaa', display: 'block', marginBottom: 5 }}>
                CF API Token
              </label>
              <input
                type="password"
                value={cfToken}
                onChange={e => setCfToken(e.target.value)}
                placeholder="cf_xxxxx..."
                disabled={cfConnecting}
                style={{
                  width: '100%', padding: '10px 12px', background: 'rgba(0,0,0,0.4)',
                  border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8,
                  color: '#e2e8f0', fontSize: 13, outline: 'none', boxSizing: 'border-box',
                }}
              />
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 11, color: '#aaa', display: 'block', marginBottom: 5 }}>
                Cloudflare Email Adresi
              </label>
              <input
                type="email"
                value={cfEmail}
                onChange={e => setCfEmail(e.target.value)}
                placeholder="ornek@email.com"
                disabled={cfConnecting}
                style={{
                  width: '100%', padding: '10px 12px', background: 'rgba(0,0,0,0.4)',
                  border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8,
                  color: '#e2e8f0', fontSize: 13, outline: 'none', boxSizing: 'border-box',
                }}
              />
            </div>

            <div style={{ padding: '10px 12px', background: 'rgba(243,128,32,0.08)',
              border: '1px solid rgba(243,128,32,0.2)', borderRadius: 8, fontSize: 11,
              color: '#fa8506', marginBottom: 16 }}>
              ⚠️ Token CF dashboard &rarr; My Profile &rarr; API Tokens&apos;tan oluşturulur. Workers AI scope&apos;u zorunlu.
            </div>

            <button
              onClick={() => void handleCFConnect()}
              disabled={cfConnecting || cfConnected}
              style={{
                width: '100%', padding: '12px', borderRadius: 8, border: 'none',
                background: cfConnected ? '#10b981' : 'linear-gradient(135deg, #f38020, #fa8506)',
                color: 'white', fontWeight: 600, fontSize: 14, cursor: cfConnecting ? 'wait' : 'pointer',
                opacity: cfConnecting ? 0.6 : 1,
              }}
            >
              {cfConnecting ? 'Bağlanıyor...' : cfConnected ? '✓ Bağlandı' : 'Bağlan & Test Et'}
            </button>
          </div>
        </div>
      )}

      <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
    </div>
  )
}
